import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { Field, Input, Select, FormGrid, SectionLabel, SaveMessage, Card, CardHeader, SubmitBtn, SecondaryBtn, EmptyState, RecordMeta, Badge, Avatar, STATES, INDUSTRIES, COURSES, ENROLLMENT_TYPES, REFERRAL_SOURCES } from './Shared';

const EMPTY = { student_name: '', email: '', phone: '', state: '', employer: '', employer_industry: '', employment_status: 'Employed — other role', referral_source: '', course: 'FWA Level 1 — Full certification', enrollment_type: 'Individual', placement_interest: 'no', ongoing_fw_need: 'unknown' };
const EMP_STATUSES = ['Employed — fire watch related role','Employed — other role','Self-employed / contractor','Seeking employment'];
const PLACEMENT_OPTS = [{value:'no',label:'No'},{value:'yes',label:'Yes — route to placement pipeline'},{value:'maybe',label:'Maybe — follow up in 30 days'}];
const ONGOING_OPTS = [{value:'unknown',label:'Unknown'},{value:'yes',label:'Yes — flag for Operations handoff'},{value:'no',label:'No'}];

export default function AcademyForm({ profile, hasFullAccess }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ show: false, text: '', error: false });
  const [records, setRecords] = useState([]);

  useEffect(() => { loadRecords(); }, []);

  async function loadRecords() {
    const { data } = await supabase.from('enrollments').select('*').order('created_at', { ascending: false });
    setRecords(data || []);
  }

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  async function save() {
    if (!form.student_name) { setMsg({ show: true, text: 'Student name is required.', error: true }); return; }
    setLoading(true);
    const { error } = await supabase.from('enrollments').insert({ ...form, submitted_by: profile.id, submitted_by_name: profile.full_name || profile.email });
    if (!error && form.student_name) {
      const ctype = form.placement_interest === 'yes' ? 'specialist' : form.enrollment_type.includes('Corporate') ? 'corporate' : 'client';
      await supabase.from('contacts').insert({ full_name: form.student_name, company: form.employer, email: form.email, phone: form.phone, state: form.state, contact_type: ctype, source_entity: 'academy', submitted_by: profile.id, submitted_by_name: profile.full_name || profile.email });
    }
    setLoading(false);
    if (error) { setMsg({ show: true, text: 'Error: ' + error.message, error: true }); return; }
    const flags = [];
    if (form.placement_interest === 'yes') flags.push('Routed to placement pipeline');
    if (form.ongoing_fw_need === 'yes') flags.push('Flagged for Operations handoff');
    if (form.enrollment_type.includes('10+')) flags.push('Corporate 10+ — priority handoff');
    setMsg({ show: true, text: 'Saved.' + (flags.length ? ' ' + flags.join(' · ') : ''), error: false });
    setForm(EMPTY);
    loadRecords();
    setTimeout(() => setMsg({ show: false }), 5000);
  }

  return (
    <div>
      <Card>
        <SectionLabel>STUDENT DETAILS</SectionLabel>
        <FormGrid>
          <Field label="Full name"><Input value={form.student_name} onChange={set('student_name')} placeholder="Alex Johnson" /></Field>
          <Field label="Email"><Input type="email" value={form.email} onChange={set('email')} placeholder="alex@company.com" /></Field>
          <Field label="Phone"><Input type="tel" value={form.phone} onChange={set('phone')} /></Field>
          <Field label="State"><Select value={form.state} onChange={set('state')} options={STATES} placeholder="Select state" /></Field>
        </FormGrid>
        <SectionLabel>EMPLOYER & MARKET INTELLIGENCE</SectionLabel>
        <FormGrid>
          <Field label="Employer name"><Input value={form.employer} onChange={set('employer')} placeholder="ABC Property Management" /></Field>
          <Field label="Employer industry"><Select value={form.employer_industry} onChange={set('employer_industry')} options={INDUSTRIES} placeholder="Select industry" /></Field>
          <Field label="Employment status"><Select value={form.employment_status} onChange={set('employment_status')} options={EMP_STATUSES} /></Field>
          <Field label="Referral source"><Select value={form.referral_source} onChange={set('referral_source')} options={REFERRAL_SOURCES} placeholder="Select source" /></Field>
          <Field label="Course enrolled"><Select value={form.course} onChange={set('course')} options={COURSES} /></Field>
          <Field label="Enrollment type"><Select value={form.enrollment_type} onChange={set('enrollment_type')} options={ENROLLMENT_TYPES} /></Field>
          <Field label="Specialist placement interest"><Select value={form.placement_interest} onChange={set('placement_interest')} options={PLACEMENT_OPTS} /></Field>
          <Field label="Ongoing fire watch need?"><Select value={form.ongoing_fw_need} onChange={set('ongoing_fw_need')} options={ONGOING_OPTS} /></Field>
        </FormGrid>
        <div className="form-actions">
          <SecondaryBtn onClick={() => setForm(EMPTY)}>Clear</SecondaryBtn>
          <SubmitBtn onClick={save} loading={loading}>Save enrollment</SubmitBtn>
        </div>
        <SaveMessage {...msg} />
      </Card>

      <Card>
        <CardHeader title="Enrollment records" hint={records.length + ' total'} />
        {!records.length ? <EmptyState text="No enrollments yet." /> :
          records.map(r => (
            <div key={r.id} className="list-row">
              <Avatar name={r.student_name} colorClass="avatar-green" />
              <div className="list-row-info">
                <div className="list-row-name">{r.student_name || '—'}</div>
                <div className="list-row-meta">{r.employer || 'No employer'} · {r.state || '—'} · {r.course}</div>
                {hasFullAccess && <RecordMeta submitter={r.submitted_by_name} date={r.created_at} />}
              </div>
              <div className="tag-row">
                {r.placement_interest === 'yes' && <Badge variant="green">Placement</Badge>}
                {r.placement_interest === 'maybe' && <Badge variant="amber">Maybe</Badge>}
                {r.ongoing_fw_need === 'yes' && <Badge variant="amber">Ops lead</Badge>}
                {r.enrollment_type && r.enrollment_type.includes('Corporate') && <Badge variant="blue">Corporate</Badge>}
              </div>
            </div>
          ))
        }
      </Card>
    </div>
  );
}
